import { CTASection } from '@/components/landing/CTASection'
import { BandReveal, HeroReveal, InteractiveDots, ScrollReveal, TextReveal } from '@/components/motion'
import {
  CapabilityGrid,
  DockMarquee,
  ImpactRow,
  StatsRow,
  StickyFeatures,
  productTiles,
} from '@/components/landing/service'
import { UseCaseVisual } from './fragments'
import { PlatformFaq, TrialButton, splitLastWord, type LayoutProps } from './shared'

/**
 * Templates ("creative" layout, blue2purple layout 2 "creative-content"):
 * white hero with a dock-magnify marquee of product UI, pink stats band,
 * pinned use cases drawn as UI fragments, a capability grid with business
 * impact, FAQ beside the platform index, and the purple CTA band.
 */
export function TemplatesLayout({ feature }: LayoutProps) {
  const [vpLead, vpLast] = splitLastWord(feature.valueProposition.title)

  return (
    <>
      {/* 1. Hero: pink name box, 5rem H1, quote + button row, then the dock marquee */}
      <section className="overflow-hidden pt-16 md:pt-24 lg:pt-28">
        <div className="landing-container">
          <div className="lg:mx-auto lg:w-[80%]">
            <HeroReveal delay={0}>
              <p className="landing-text-display-xl">
                <span className="inline-block -rotate-1 bg-[#ff00f2] px-[0.25em] text-white">{feature.shortLabel}.</span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.1}>
              <h1 className="landing-text-hero mt-4 text-[var(--color-landing-gray-900)] dark:text-white md:mt-6">
                <span className="block">{feature.heroLines[0]}</span>{' '}
                <span className="block">{feature.heroLines[1]}</span>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2} className="mt-10 flex flex-col gap-8 md:mt-12 md:flex-row md:items-center md:gap-12">
              <p className="landing-text-body-lg max-w-[62ch] text-pretty border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)] md:text-xl">
                {feature.subheadline}
              </p>
              <TrialButton className="self-start md:self-center" />
            </HeroReveal>
          </div>
        </div>
        <HeroReveal delay={0.35} distance={40}>
          <DockMarquee className="mt-8 md:mt-12" tiles={productTiles('proposal')} />
        </HeroReveal>
      </section>

      {/* 2. Stats band: pink, display-size figures */}
      <BandReveal>
        <section aria-label={`${feature.shortLabel} in numbers`} className="landing-section-lg relative overflow-hidden bg-[#ff00f2]">
          <InteractiveDots />
          <div className="landing-container relative z-10">
            <div className="lg:mx-auto lg:w-[80%]">
              <StatsRow stats={feature.heroStats} size="display" />
            </div>
          </div>
        </section>
      </BandReveal>

      {/* 3. Sticky features: use cases drawn as product UI */}
      <section aria-labelledby="use-cases-heading" className="landing-section">
        <div className="landing-container mb-12 lg:mb-0">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="use-cases-heading">
              How teams use <span className="landing-highlight landing-highlight--secondary">{feature.shortLabel}</span>
            </span>
          </TextReveal>
        </div>
        <StickyFeatures
          accent="purple"
          steps={feature.useCases.map((useCase) => ({
            title: useCase.title,
            description: useCase.description,
            extra: (
              <span className="inline-block bg-[var(--color-landing-gray-100)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[var(--color-landing-gray-700)] dark:bg-[#1c1c26] dark:text-[var(--color-landing-gray-300)]">
                {useCase.industry}
              </span>
            ),
            visual: (active: boolean) => <UseCaseVisual useCase={useCase} active={active} />,
          }))}
        />
      </section>

      {/* 4. Capability grid + business impact */}
      <section className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
            <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
              {vpLead} <span className="landing-highlight">{vpLast}</span>
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[52ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {feature.valueProposition.description}
              </p>
            </ScrollReveal>
          </div>

          <div className="mt-16 md:mt-20">
            <CapabilityGrid capabilities={feature.capabilities} />
          </div>
          <div className="mt-20 md:mt-24">
            <h3 className="mb-10 font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] text-[var(--color-landing-gray-900)] dark:text-white">
              Business impact
            </h3>
            <ImpactRow benefits={feature.benefits} />
          </div>
        </div>
      </section>

      {/* 5. FAQ + platform index */}
      <PlatformFaq feature={feature} />

      {/* 6. Purple CTA band */}
      <CTASection
        variant="accent"
        align="left"
        className="bg-[var(--color-landing-secondary)] dark:bg-[var(--color-landing-secondary)]"
        title={feature.ctaTitle}
        description={feature.ctaDescription}
        buttons={[
          { label: 'Start free trial', href: '/register', variant: 'primary' },
          { label: 'Book a demo', href: '/contact', variant: 'outline' },
        ]}
      />
    </>
  )
}
